import { h, render } from "preact";
import type { WidgetPosition } from "./types";
import { Widget } from "./components/Widget";
import { fetchWidgetConfig } from "./api/client";
import { startConsoleCapture } from "./capture/console";
import { startNetworkCapture } from "./capture/network";
import { startErrorCapture } from "./capture/errors";
// Bun menyisipkan isi berkas sebagai string lewat `type: "text"` — padanan
// `?inline` milik Vite. CSS-nya masuk ke shadow root, bukan ke halaman.
import styles from "./styles/widget.css" with { type: "text" };

type BootConfig = {
  apiBase: string;
  projectKey: string;
  /** Dari server saja — tag skrip tidak boleh menentukan hak baca. */
  publicList?: boolean;
  position: WidgetPosition;
  primaryColor?: string;
  buttonLabel?: string;
  appTitle?: string;
};

/**
 * Setelan dari SERVER menimpa yang dari tag skrip.
 *
 * Yang memasang cuplikan di situs klien adalah orang klien; yang memutuskan rupa
 * widget adalah tim Maua, di ProjectProfile. Kalau tag skrip yang menang, setiap
 * perubahan setelan menuntut mereka menyunting HTML lagi — dan itu tidak akan
 * terjadi. `data-*` tetap dihormati sebagai bawaan supaya widget tetap muncul
 * benar sebelum panggilan config selesai, dan tetap muncul kalau ia gagal.
 */
async function withServerConfig(cfg: BootConfig): Promise<BootConfig> {
  const remote = await fetchWidgetConfig({ apiBase: cfg.apiBase, projectKey: cfg.projectKey });
  if (!remote) return cfg;
  return {
    ...cfg,
    position: VALID_POSITIONS.includes(remote.position as WidgetPosition) ? (remote.position as WidgetPosition) : cfg.position,
    primaryColor: remote.primaryColor ?? cfg.primaryColor,
    buttonLabel: remote.buttonLabel ?? cfg.buttonLabel,
    appTitle: remote.appTitle ?? cfg.appTitle,
    // Sengaja TIDAK memakai `?? cfg.publicList`: hak baca hanya boleh datang dari
    // server, jadi config yang gagal dimuat berarti tertutup, bukan terbuka.
    publicList: remote.publicList === true,
  };
}

const VALID_POSITIONS: WidgetPosition[] = ["bottom-right", "bottom-left", "top-right", "top-left"];

function readBootConfig(): BootConfig | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-project-key]");
  if (!script) {
    console.warn("[maua-feedback] widget script tag not found");
    return null;
  }
  const projectKey = script.dataset.projectKey ?? "";
  if (!projectKey) {
    console.warn("[maua-feedback] data-project-key is required on the script tag");
    return null;
  }

  // Derive API base from the widget script URL by default.
  let apiBase = script.dataset.apiBase ?? "";
  if (!apiBase) {
    try {
      apiBase = new URL(script.src).origin;
    } catch {
      apiBase = location.origin;
    }
  }

  const positionRaw = script.dataset.position as WidgetPosition | undefined;
  const position: WidgetPosition =
    positionRaw && VALID_POSITIONS.includes(positionRaw) ? positionRaw : "bottom-right";

  return {
    apiBase,
    projectKey,
    position,
    primaryColor: script.dataset.primaryColor,
    buttonLabel: script.dataset.buttonLabel,
    appTitle: script.dataset.appTitle,
  };
}

function mount(cfg: BootConfig): void {
  // Single-instance guard so multiple script tags don't double-mount.
  if (document.getElementById("maua-feedback-root")) return;

  const host = document.createElement("div");
  host.id = "maua-feedback-root";
  host.setAttribute("data-position", cfg.position);
  host.setAttribute("data-maua-feedback-ignore", "true");
  if (cfg.primaryColor) host.style.setProperty("--mf-primary", cfg.primaryColor);

  // Mount OUTSIDE <body> — as a direct child of <html> — instead of inside it.
  //
  // Why: SPA frameworks (React/React Router, Vue, Svelte) reconcile the DOM by
  // tracking the children of their mount container. Many apps mount into, or
  // portal modals/toasts/tooltips into, `document.body`. A foreign node we add
  // to <body> sits among the children the framework is tracking and breaks its
  // sibling bookkeeping, producing errors like:
  //   NotFoundError: Failed to execute 'insertBefore' on 'Node': The node
  //   before which the new node is to be inserted is not a child of this node.
  // Living under <html> (we're position:fixed in a shadow root, so it renders
  // identically) keeps us entirely outside any framework-managed subtree.
  // Fall back to <body> on the off chance documentElement is unavailable.
  (document.documentElement ?? document.body).appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);
  // Re-apply :host position attribute inside shadow (CSS reads it via :host selector).
  // Setting on the host element propagates correctly.

  const container = document.createElement("div");
  shadow.appendChild(container);

  // Set CSS vars on host so :host([data-position]) selectors apply.
  // Also forward primary color into the shadow root.
  if (cfg.primaryColor) {
    container.style.setProperty("--mf-primary", cfg.primaryColor);
  }

  // Start captures — never let setup break the host app.
  try {
    startConsoleCapture();
  } catch {
    /* noop */
  }
  try {
    startNetworkCapture({ ignoreOrigin: cfg.apiBase });
  } catch {
    /* noop */
  }
  try {
    startErrorCapture();
  } catch {
    /* noop */
  }

  render(
    h(Widget, {
      apiConfig: { apiBase: cfg.apiBase, projectKey: cfg.projectKey, publicList: cfg.publicList },
      rootElement: host,
      buttonLabel: cfg.buttonLabel,
      appTitle: cfg.appTitle,
    }),
    container,
  );
}

async function boot() {
  const raw = readBootConfig();
  if (!raw) return;
  const cfg = await withServerConfig(raw);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(cfg), { once: true });
  } else {
    mount(cfg);
  }
}

void boot();
